import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import { ADMIN_USUARIOS_PAGE } from "../../admin-sistema/routes";
import { getAllCoverageApi, AllCoverage } from "../coverageApi";
import { STORE_EVALUATION_DETAIL_PAGE } from "../routes";

import "./StoreCoveragePage.css";

const DAY_MS = 86_400_000;

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const parseYmd = (s: string) => new Date(`${s}T00:00:00.000Z`);
const ddmm = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;

/** Segunda-feira da semana do dia. */
const mondayOf = (d: Date) => new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS);

/** A semana anterior à de hoje (São Paulo): a que a cobrança de segunda confere. */
const lastWeek = () => {
  const today = parseYmd(new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" }));
  return ymd(new Date(mondayOf(today).getTime() - 7 * DAY_MS));
};

/**
 * Cobertura semanal dos supervisores de campo (plan-notificacoes-whatsapp,
 * 22/10/2026): cada supervisor com as lojas vinculadas a ele e se tiveram
 * avaliação enviada na semana (segunda a domingo). É o destino do aviso de
 * segunda-feira no sino e no WhatsApp.
 */
export function StoreCoveragePage() {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());
  const [params, setParams] = useSearchParams();
  const week = ymd(mondayOf(parseYmd(params.get("semana") ?? lastWeek())));
  const onlyMissing = params.get("faltas") === "1";

  const [data, setData] = useState<AllCoverage | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getAllCoverageApi(week));
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível carregar a cobertura da semana.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => {
    load();
  }, [load]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const shiftWeek = (weeks: number) => setParam("semana", ymd(new Date(parseYmd(week).getTime() + weeks * 7 * DAY_MS)));
  const isFuture = parseYmd(week).getTime() + 7 * DAY_MS > Date.now();

  const totals = useMemo(() => {
    const sups = data?.supervisors ?? [];
    return {
      supervisors: sups.length,
      complete: sups.filter((s) => s.done === s.total).length,
      stores: sups.reduce((n, s) => n + s.total, 0),
      done: sups.reduce((n, s) => n + s.done, 0),
    };
  }, [data]);

  const rows = (data?.supervisors ?? []).filter((s) => !onlyMissing || s.done < s.total);

  return (
    <div className="covPage">
      <main className="covPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Cobertura semanal" },
          ]}
          title="Cobertura semanal"
          subtitle="Lojas vinculadas a cada supervisor de campo e quais tiveram avaliação enviada na semana."
          onBack={goBack}
          backLabel="Voltar para Operações"
        />

        <section className="ecoCard covPage__toolbar" aria-label="Semana">
          <div className="covPage__week">
            <button type="button" className="ecoBtn ecoBtn--icon" onClick={() => shiftWeek(-1)} aria-label="Semana anterior">
              <EcoIcon name="chevronLeft" size={18} />
            </button>
            <span className="covPage__weekLabel">
              Semana de {ddmm(week)} a {ddmm(ymd(new Date(parseYmd(week).getTime() + 6 * DAY_MS)))}
            </span>
            <button
              type="button"
              className="ecoBtn ecoBtn--icon"
              onClick={() => shiftWeek(1)}
              disabled={isFuture}
              aria-label="Próxima semana"
            >
              <EcoIcon name="chevronRight" size={18} />
            </button>
          </div>
          <label className="covPage__toggle">
            <input type="checkbox" checked={onlyMissing} onChange={(e) => setParam("faltas", e.target.checked ? "1" : null)} />
            Só quem tem loja sem visita
          </label>
        </section>

        {data ? (
          <div className="covPage__stats">
            <div className="ecoCard covPage__stat">
              <span className="covPage__statValue">
                {totals.done} de {totals.stores}
              </span>
              <span className="covPage__statLabel">lojas visitadas</span>
            </div>
            <div className="ecoCard covPage__stat">
              <span className="covPage__statValue">
                {totals.complete} de {totals.supervisors}
              </span>
              <span className="covPage__statLabel">supervisores com todas as lojas</span>
            </div>
          </div>
        ) : null}

        {loading && !data ? <div className="ecoCard ecoEmpty">Carregando…</div> : null}

        {data && !data.supervisors.length ? (
          <div className="ecoCard ecoEmpty">
            Nenhum supervisor de campo tem loja vinculada. As lojas de cada um se vinculam em{" "}
            <button type="button" className="ecoLinkBtn" onClick={() => navigate(ADMIN_USUARIOS_PAGE())}>
              Administração → Gestão de usuários
            </button>
            .
          </div>
        ) : null}

        {data && data.supervisors.length && !rows.length ? (
          <div className="ecoCard ecoEmpty">Todos os supervisores visitaram todas as lojas nesta semana.</div>
        ) : null}

        <div className="covPage__grid">
          {rows.map((sup) => (
            <section key={sup.user.id} className="ecoCard covPage__sup" aria-label={sup.user.nomeCompleto}>
              <header className="covPage__supHead">
                <strong>{sup.user.nomeCompleto}</strong>
                <span className={`ecoPill ${sup.done === sup.total ? "" : "ecoPill--off"}`}>
                  {sup.done} de {sup.total}
                </span>
              </header>
              <ul className="covPage__stores">
                {sup.stores.map((store) => (
                  <li key={store.id} className={`covPage__store ${store.done ? "is-done" : "is-missing"}`}>
                    <EcoIcon name={store.done ? "check" : "x"} size={16} />
                    <span className="covPage__storeName">{store.nomeFantasia}</span>
                    {store.done && store.evaluationId ? (
                      <button
                        type="button"
                        className="ecoLinkBtn covPage__storeWhen"
                        onClick={() => navigate(STORE_EVALUATION_DETAIL_PAGE(store.evaluationId!))}
                      >
                        {store.referenceDate ? ddmm(store.referenceDate) : "Ver"}
                      </button>
                    ) : (
                      <span className="covPage__storeWhen">Sem visita</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
